import { siteConfig } from './siteConfig'

const baseUrl = siteConfig.seo.baseUrl
const ORG_ID = siteConfig.seo.organizationId
const PERSON_ID = siteConfig.seo.founderPersonId
const WEBSITE_ID = siteConfig.seo.websiteId

function getMaxCapacity(batchSize?: string) {
  const values = batchSize?.match(/\d+/g)
  return values?.[values.length - 1]
}

type CourseStructuredDataProps = {
  name: string
  description: string
  urlPath: string
  providerName: string
  price: string
  currency?: string
  category: string
  level: string
  language: string
  instructor: string
  duration?: string
  schedule?: string
  batchSize?: string
  status?: 'open' | 'coming-soon'
  startDate?: string
}

type FAQItem = {
  question: string
  answer: string
}

type BreadcrumbItem = {
  name: string
  url: string
}

type StaticPageStructuredDataProps = {
  title: string
  description: string
  path: string
  schemaType?: 'AboutPage' | 'ContactPage' | 'CollectionPage' | 'WebPage'
}

type CourseListItem = {
  title: string
  slug: string
  description: string
  category: string
  level?: string
  language?: string
  duration?: string
  price?: string
  earlyBirdPrice?: string
  discountedPrice?: string
  status?: 'open' | 'coming-soon'
}

export function JsonLd({ data }: { data: object }) {
  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(data).replace(/</g, '\\u003c') }}
    />
  )
}

export function CourseItemListStructuredData({ courses }: { courses: CourseListItem[] }) {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'Skillo Academy Courses',
    itemListElement: courses.map((course, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      item: {
        '@type': 'Course',
        name: course.title,
        description: course.description,
        url: `${baseUrl}/courses/${course.slug}`,
        provider: { '@id': ORG_ID },
        about: course.category,
        educationalLevel: course.level,
        inLanguage: course.language,
        ...(course.duration ? { timeRequired: course.duration } : {}),
        ...(course.price
          ? {
              offers: {
                '@type': 'Offer',
                category: 'Education',
                priceCurrency: 'BDT',
                price: (course.earlyBirdPrice || course.discountedPrice || course.price).replace(
                  /[^\d.]/g,
                  '',
                ),
                availability:
                  course.status === 'coming-soon'
                    ? 'https://schema.org/PreOrder'
                    : 'https://schema.org/InStock',
                url: `${baseUrl}/courses/${course.slug}`,
              },
            }
          : {}),
      },
    })),
  }

  return <JsonLd data={schema} />
}

export function StaticPageStructuredData({
  title,
  description,
  path,
  schemaType = 'WebPage',
}: StaticPageStructuredDataProps) {
  const canonicalUrl = new URL(path, baseUrl).toString()
  const schema = {
    '@context': 'https://schema.org',
    '@type': schemaType,
    name: title,
    headline: title,
    description,
    url: canonicalUrl,
    isPartOf: { '@id': WEBSITE_ID },
    about: { '@id': ORG_ID },
    publisher: { '@id': ORG_ID },
    inLanguage: siteConfig.seo.language,
  }

  return <JsonLd data={schema} />
}

export function CourseStructuredData({
  name,
  description,
  urlPath,
  providerName,
  price,
  currency = 'BDT',
  category,
  level,
  language,
  instructor,
  duration,
  schedule,
  batchSize,
  status,
  startDate,
}: CourseStructuredDataProps) {
  const canonicalUrl = new URL(urlPath, baseUrl).toString()
  const numericPrice = price.replace(/[^\d.]/g, '')
  const maxCapacity = getMaxCapacity(batchSize)

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Course',
    name,
    description,
    url: canonicalUrl,
    provider: {
      '@type': 'EducationalOrganization',
      '@id': ORG_ID,
      name: providerName,
      sameAs: baseUrl,
    },
    inLanguage: language,
    educationalLevel: level,
    about: category,
    teaches: category,
    instructor: {
      '@type': 'Person',
      name: instructor,
      ...(instructor === 'Minhaj Sadik'
        ? { '@id': PERSON_ID, sameAs: 'https://minhajsadik.com' }
        : {}),
    },
    offers: {
      '@type': 'Offer',
      category: 'Education',
      priceCurrency: currency,
      price: numericPrice,
      url: canonicalUrl,
      availability:
        status === 'coming-soon' ? 'https://schema.org/PreOrder' : 'https://schema.org/InStock',
    },
    hasCourseInstance: {
      '@type': 'CourseInstance',
      courseMode: ['Online', 'Live cohort'],
      ...(duration ? { courseWorkload: duration } : {}),
      ...(schedule ? { courseSchedule: schedule } : {}),
      ...(maxCapacity ? { maximumAttendeeCapacity: maxCapacity } : {}),
      ...(startDate ? { startDate } : {}),
      inLanguage: language,
      location: {
        '@type': 'VirtualLocation',
        url: canonicalUrl,
      },
      instructor: {
        '@type': 'Person',
        name: instructor,
      },
    },
  }

  return <JsonLd data={schema} />
}

export function FAQStructuredData({ items }: { items: FAQItem[] }) {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  }

  return <JsonLd data={schema} />
}

export function BreadcrumbStructuredData({ items }: { items: BreadcrumbItem[] }) {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url.startsWith('http') ? item.url : new URL(item.url, baseUrl).toString(),
    })),
  }

  return <JsonLd data={schema} />
}
